import Markdown from 'react-markdown';
import type { ChatMessage } from '../../../types/chatMessage';
import { type ChatMessageAttachment } from './chatMessageAttachmentUtils';
import { LiksChatMessageFileAttachment } from './LiksChatMessageFileAttachment';
import { LiksChatMessageGeneratedImage } from './LiksChatMessageGeneratedImage';

export type LiksChatMessageBubbleContentProps = {
  message: ChatMessage;
  isAi: boolean;
  hasTextContent: boolean;
  isStreamingReply: boolean;
  assistantImageAttachments: ChatMessageAttachment[];
  userImageAttachments: ChatMessageAttachment[];
  userFileAttachments: ChatMessageAttachment[];
  generatedImageAltLabel: string;
  downloadImageLabel: string;
  downloadAttachmentLabel: string;
  expandImageLabel: string;
  closeImagePreviewLabel: string;
  imageLoadingLabel: string;
  enableSaveToGallery: boolean;
  saveToGalleryLabel?: string;
};

export function LiksChatMessageBubbleContent({
  message,
  isAi,
  hasTextContent,
  isStreamingReply,
  assistantImageAttachments,
  userImageAttachments,
  userFileAttachments,
  generatedImageAltLabel,
  downloadImageLabel,
  downloadAttachmentLabel,
  expandImageLabel,
  closeImagePreviewLabel,
  imageLoadingLabel,
  enableSaveToGallery,
  saveToGalleryLabel,
}: LiksChatMessageBubbleContentProps) {
  return (
    <>
      {isAi
        ? hasTextContent && (
            <div className="liks-chat-message__content liks-chat-message__content--markdown">
              <Markdown>{message.content}</Markdown>
              {isStreamingReply && (
                <span className="liks-chat-message__stream-cursor" aria-hidden>
                  |
                </span>
              )}
            </div>
          )
        : hasTextContent && (
            <p className="liks-chat-message__content">{message.content}</p>
          )}

      {isAi && assistantImageAttachments.length > 0 && (
        <div className="liks-chat-message__images">
          {assistantImageAttachments.map((attachment) => (
            <LiksChatMessageGeneratedImage
              key={attachment.id}
              sessionId={message.sessionId}
              messageId={message.id}
              attachmentId={attachment.id}
              galleryImageId={attachment.galleryImageId}
              alt={
                attachment.title?.trim() ||
                attachment.fileName?.trim() ||
                attachment.documentName?.trim() ||
                generatedImageAltLabel
              }
              title={attachment.title}
              imageFormat={attachment.imageFormat ?? message.imageFormat}
              imageAspectRatio={
                attachment.imageAspectRatio ?? message.imageAspectRatio
              }
              fileName={
                attachment.fileName ?? attachment.documentName ?? undefined
              }
              downloadLabel={downloadImageLabel}
              expandLabel={expandImageLabel}
              closePreviewLabel={closeImagePreviewLabel}
              loadingLabel={imageLoadingLabel}
              enableSaveToGallery={enableSaveToGallery}
              saveToGalleryLabel={saveToGalleryLabel}
            />
          ))}
        </div>
      )}

      {!isAi && userImageAttachments.length > 0 && (
        <div className="liks-chat-message__images">
          {userImageAttachments.map((attachment) => (
            <LiksChatMessageGeneratedImage
              key={attachment.id}
              sessionId={message.sessionId}
              messageId={message.id}
              attachmentId={attachment.id}
              alt={
                attachment.title?.trim() ||
                attachment.fileName?.trim() ||
                attachment.documentName?.trim() ||
                generatedImageAltLabel
              }
              title={attachment.title}
              fileName={
                attachment.fileName ?? attachment.documentName ?? undefined
              }
              downloadLabel={downloadAttachmentLabel}
              expandLabel={expandImageLabel}
              closePreviewLabel={closeImagePreviewLabel}
              loadingLabel={imageLoadingLabel}
            />
          ))}
        </div>
      )}

      {!isAi && userFileAttachments.length > 0 && (
        <div
          className="liks-chat-message__attachments"
          aria-label={downloadAttachmentLabel}
        >
          {userFileAttachments.map((attachment) => (
            <LiksChatMessageFileAttachment
              key={attachment.id}
              sessionId={message.sessionId}
              messageId={message.id}
              attachment={attachment}
              downloadLabel={downloadAttachmentLabel}
              loadingLabel={imageLoadingLabel}
            />
          ))}
        </div>
      )}
    </>
  );
}
